import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import { StorageService } from 'ngx-webstorage-service';

import { BroadcastChannelActions } from '@common/model/broadcast-channel-actions';
import { AppSessionStorageService, AppStorageService } from '@common/services/app-storage.service';
import { BroadcastChannelService } from '@common/services/broadcast-channel.service';

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly bundleRe = /main-\w+\.js/;
  private readonly document = inject(DOCUMENT);
  private readonly http = inject(HttpClient);
  private readonly broadcastChannelService = inject(BroadcastChannelService);
  private readonly storageService = inject<StorageService>(AppStorageService);
  private readonly sessionStorageService = inject<StorageService>(AppSessionStorageService);

  private readonly currentMainSrc = this.document
    .querySelector<HTMLScriptElement>('script[src*="main-"]')
    ?.getAttribute('src') ?? null;

  private readonly updateAvailable = new BehaviorSubject(false);
  readonly isUpdateAvailable$ = this.updateAvailable.asObservable();

  private readonly pollLockKey = 'fbo_version_poll_lock';
  private readonly pollIntervalMs = 5 * 60 * 1000;
  private readonly pollLockTimeoutMs = 6 * 60 * 1000; // outlasts the 5-min interval

  // Chromium restores tabs from the HTTP cache without revalidating the HTML, whatever its
  // Cache-Control says, so a restored tab can start on a build several deploys old.
  private readonly freshStartWindowMs = 10_000;
  private readonly staleStartReloadKey = 'phx:stale-start-reload-from';
  private readonly interactionEvents = ['pointerdown', 'keydown'] as const;
  private hasUserInteracted = false;
  private readonly markUserInteraction = (): void => {
    this.hasUserInteracted = true;
  };

  private tryAcquirePollLock(): string | null {
    const existing = this.storageService.get(this.pollLockKey) as string | null;
    if (existing != null) {
      const ts = parseInt(existing.split(':')[0], 10);
      if (!Number.isNaN(ts) && Date.now() - ts < this.pollLockTimeoutMs) {
        return null; // another tab holds the lock
      }
    }
    const owner = Math.random().toString(36).slice(2);
    const value = `${Date.now()}:${owner}`;
    this.storageService.set(this.pollLockKey, value);
    return (this.storageService.get(this.pollLockKey) as string | null) === value ? owner : null;
  }

  private releasePollLock(owner: string): void {
    if ((this.storageService.get(this.pollLockKey) as string | null)?.endsWith(`:${owner}`)) {
      this.storageService.remove(this.pollLockKey);
    }
  }

  start(): void {
    // Only poll in builds where Angular has hashed the main bundle (i.e. not ng serve)
    if (!this.currentMainSrc?.match(this.bundleRe)) {
      return;
    }

    this.interactionEvents.forEach(type => this.document.addEventListener(type, this.markUserInteraction, { capture: true }));
    this.fetchLatestMainSrc().subscribe(latestMainSrc => {
      this.interactionEvents.forEach(type => this.document.removeEventListener(type, this.markUserInteraction, { capture: true }));
      if (!this.isNewer(latestMainSrc)) {
        return;
      }
      if (this.canReloadStaleStart()) {
        this.sessionStorageService.set(this.staleStartReloadKey, this.currentMainSrc);
        this.reloadThisTab();
        return;
      }
      this.updateAvailable.next(true);
    });

    timer(this.pollIntervalMs, this.pollIntervalMs)
      .pipe(
        switchMap(() => {
          const owner = this.tryAcquirePollLock();
          if (!owner) {
            return EMPTY; // another tab is handling this tick
          }
          return this.fetchLatestMainSrc().pipe(
            tap(() => this.releasePollLock(owner))
          );
        }),
        filter(latestMainSrc => this.isNewer(latestMainSrc))
      )
      .subscribe(() => this.updateAvailable.next(true));
  }

  reload(): void {
    this.broadcastChannelService.sendMessage({ action: BroadcastChannelActions.channelTabsRefresh });
    this.reloadThisTab();
  }

  private reloadThisTab(): void {
    window.location.reload();
  }

  private fetchLatestMainSrc(): Observable<string | null> {
    return this.http.get(`index.html?_=${Date.now()}`, { responseType: 'text' }).pipe(
      map(html => html.match(this.bundleRe)?.[0] ?? null),
      catchError(() => of(null))
    );
  }

  private isNewer(latestMainSrc: string | null): boolean {
    return latestMainSrc !== null && latestMainSrc !== this.currentMainSrc;
  }

  // Reloading is only safe before the user has done anything on the page — a reload bypasses the
  // pending-changes guards — and only once per stale build, so a reload that comes back on the
  // same build falls back to the banner.
  private canReloadStaleStart(): boolean {
    const isFreshStart = performance.now() < this.freshStartWindowMs;
    const alreadyReloadedFromThisBuild = this.sessionStorageService.get(this.staleStartReloadKey) === this.currentMainSrc;
    return isFreshStart && !this.hasUserInteracted && !alreadyReloadedFromThisBuild;
  }
}
